import { useState, useMemo, useCallback } from 'react';

import { ServiceFilter } from './fragments/ServiceListFilters';
import { StatFilter } from './fragments/ServiceStatsOverview';
import { SortOption } from './MobileServices.utils';

interface FilterChip {
  label: string;
  key: string;
}

export const useMobileServiceFilters = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState<ServiceFilter>('all');
  const [urgentFilter, setUrgentFilter] = useState(false);
  const [todayFilter, setTodayFilter] = useState(false);
  const [activeStatFilter, setActiveStatFilter] = useState<StatFilter | null>('allocated');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState('open');
  const [activeSort, setActiveSort] = useState<SortOption>('newest');

  const isShowingClosed = serviceFilter === 'closed7Days' || serviceFilter === 'closed30Days';

  const handleStatClick = useCallback((filter: StatFilter) => {
    setActiveStatFilter((prev) => (prev === filter ? null : filter));
    setTodayFilter(false);
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (activeStatFilter) count++;
    if (serviceFilter !== 'all') count++;
    if (dateRangeFilter !== 'open') count++;
    if (urgentFilter) count++;
    if (todayFilter) count++;
    return count;
  }, [activeStatFilter, serviceFilter, dateRangeFilter, urgentFilter, todayFilter]);

  const activeFilterChips = useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = [];
    if (serviceFilter !== 'all') {
      const labels: Record<string, string> = {
        maintenance: 'Maintenance',
        installOrDecom: 'Install/Decom',
        closed7Days: 'Closed 7 Days',
        closed30Days: 'Closed 30 Days',
      };
      chips.push({ label: labels[serviceFilter] || serviceFilter, key: 'serviceType' });
    }
    if (activeStatFilter) {
      const statLabels: Record<string, string> = {
        allocated: 'Allocated To Me',
        depot: 'My Depot',
        zones: 'My Zones',
      };
      chips.push({ label: statLabels[activeStatFilter] || activeStatFilter, key: 'stat' });
    }
    if (urgentFilter) chips.push({ label: 'Urgent', key: 'urgent' });
    if (todayFilter) chips.push({ label: 'Today', key: 'today' });
    return chips;
  }, [serviceFilter, activeStatFilter, urgentFilter, todayFilter]);

  const handleRemoveFilterChip = useCallback((key: string) => {
    if (key === 'stat') {
      setActiveStatFilter(null);
    } else if (key === 'serviceType') {
      setServiceFilter('all');
    } else if (key === 'urgent') {
      setUrgentFilter(false);
    } else if (key === 'today') {
      setTodayFilter(false);
    }
  }, []);

  const handleApplyFilters = useCallback(
    (filters: {
      serviceType: ServiceFilter;
      dateRange: string;
      urgent: boolean;
      today: boolean;
    }) => {
      setActiveStatFilter(null);
      setServiceFilter(filters.serviceType);
      setDateRangeFilter(filters.dateRange);
      setUrgentFilter(filters.urgent);
      setTodayFilter(filters.today);
    },
    []
  );

  const handleClearAllFilters = useCallback(() => {
    setActiveStatFilter(null);
    setServiceFilter('all');
    setDateRangeFilter('open');
    setUrgentFilter(false);
    setTodayFilter(false);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    serviceFilter,
    urgentFilter,
    todayFilter,
    activeStatFilter,
    isFilterModalOpen,
    setIsFilterModalOpen,
    dateRangeFilter,
    isShowingClosed,
    handleStatClick,
    activeFilterCount,
    activeFilterChips,
    handleRemoveFilterChip,
    handleApplyFilters,
    handleClearAllFilters,
    activeSort,
    setActiveSort,
  };
};
